import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import MissionsScreen from "../../screens/user/Missions";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { MockedAuthManager } from "../../contexts/AuthManager";
import {
    buildClient,
    buildClientInfo,
    buildClientProfile,
    buildContract,
    buildGraphqlError,
    buildIjmGroup,
    buildMission,
    buildUser,
    buildWorkerInfo,
    buildWorkerProfile,
} from "../factories";
import {
    ContractSignatureStatus,
    ContractState,
    ProfileType,
    TerminatedByType,
} from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

const StoryComponent = ({
    profileType,
    hasTalent,
    hasMissions,
    hasError,
}: {
    profileType: ProfileType;
    hasTalent: boolean;
    hasMissions: boolean;
    hasError: boolean;
}) => {
    const mocks = [
        {
            request: {
                query: FIND_MY_MISSIONS,
                variables: () => true,
            },
            error: hasError ? buildGraphqlError("Fake mission error") : undefined,
            result: () => ({
                data: {
                    findMyMissions: {
                        elements: hasMissions
                            ? [
                                  buildMission({
                                      id: "mission-10",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({ id: "client" }),
                                  }),
                                  buildMission({
                                      id: "mission-20",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({ id: "client" }),
                                      currentContract: buildContract({
                                          state: ContractState.PendingAcceptation,
                                          signatureStatus: ContractSignatureStatus.NeedSelfSign,
                                      }),
                                  }),
                                  buildMission({
                                      id: "mission-30",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({
                                          id: "client2",
                                          profileName: "Elon Musk",
                                      }),
                                      currentContract: buildContract({
                                          state: ContractState.PendingAcceptation,
                                          signatureStatus: ContractSignatureStatus.NeedSelfSign,
                                      }),
                                  }),
                                  buildMission({
                                      id: "mission-40",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({ id: "client" }),
                                      currentContract: buildContract({
                                          state: ContractState.PendingAcceptation,
                                          signatureStatus: ContractSignatureStatus.NeedSeal,
                                      }),
                                  }),
                                  buildMission({
                                      id: "mission-41",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({ id: "client" }),
                                      currentContract: buildContract({
                                          state: ContractState.Terminated,
                                          effectiveEndDate: "2030-01-01",
                                          terminatedBy: TerminatedByType.Client,
                                          terminationOtherReason: "Travail mal fait :-)",
                                      }),
                                  }),
                                  buildMission({
                                      id: "mission-42",
                                      clientInfo: buildClientInfo({ id: "client" }),
                                      signerInfo: buildClientInfo({ id: "client" }),
                                      currentContract: buildContract({
                                          state: ContractState.Ended,
                                          effectiveEndDate: "2023-01-01",
                                          terminatedBy: TerminatedByType.Client,
                                          terminationOtherReason: "Travail mal fait :-)",
                                      }),
                                  }),
                                  buildMission({
                                      id: "mission-50",
                                      signerInfo: buildClientInfo({ id: "client" }),
                                      currentContract: buildContract({
                                          state: ContractState.Accepted,
                                          signatureStatus: ContractSignatureStatus.NeedOtherSign,
                                      }),
                                  }),

                                  buildMission({
                                      id: "mission-60",
                                      currentContract: buildContract({
                                          endDate: null,
                                      }),
                                  }),
                              ]
                            : [],
                        count: hasMissions ? 2 : 0,
                        pageInfo: {},
                    },
                },
            }),
        },
    ];
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
                clientInfos: [buildClientInfo({ id: "client" })],
                workerProfile:
                    profileType == ProfileType.Worker
                        ? buildWorkerProfile({
                              id: "worker",
                          })
                        : null,
                clientProfiles:
                    profileType == ProfileType.Client
                        ? [
                              buildClientProfile({
                                  id: "client",
                                  clientRelationshipCount: hasTalent ? 1 : 0,
                                  client: buildClient({
                                      ijmGroup: buildIjmGroup(),
                                  }),
                              }),
                          ]
                        : [],
            }),
        [hasTalent, profileType],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType} profileId={profileType}>
                <MockedProvider mocks={mocks}>
                    <MissionsScreen />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Missions",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
        hasTalent: {
            control: {
                type: "boolean",
            },
        },
        hasMissions: {
            control: {
                type: "boolean",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        hasMissions: true,
        hasTalent: true,
        hasError: false,
    },
};

export const WorkerWithError: Story = {
    args: {
        profileType: ProfileType.Worker,
        hasMissions: true,
        hasTalent: true,
        hasError: true,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        hasMissions: true,
        hasTalent: true,
        hasError: false,
    },
};

export const ClientNoTalent: Story = {
    args: {
        profileType: ProfileType.Client,
        hasMissions: false,
        hasTalent: false,
        hasError: false,
    },
};

export const ClientNoMissions: Story = {
    args: {
        profileType: ProfileType.Client,
        hasMissions: false,
        hasTalent: true,
        hasError: false,
    },
};

export const ClientNew: Story = {
    args: {
        profileType: ProfileType.Client,
        hasMissions: false,
        hasTalent: false,
        hasError: false,
    },
};
